import { Spinner } from '@synapex/frontend-shared/ui/Loading';

import {
  AdvertiserAccount,
  Workspace,
  Owner,
  User,
  OwnerSettings,
} from '@/true-fsd/data/advertiser-account';
import {
  useUsers,
  useUpdateOwnerSettings,
  useUpdateUser,
  useDeleteUser,
  useWorkspaceUsers,
  useUpdateWorkspaceOwnerSettings,
  useUpdateWorkspaceUser,
  useDeleteWorkspaceUser,
} from '@/true-fsd/data/advertiser-account/react-query';
import { SnakeIdObj } from '@synapex/tools/typescript';
import { Table } from './Table';
import {
  TableRow,
  type TableRowProps,
  type TableRowDataProps,
} from './TableRow';
import {
  WorkspaceDescriptor,
  workspaceMethods,
} from '@synapex/shared2/entities';

export type PermissionsTableProps = {
  className?: string;
  account: AdvertiserAccount | null;
  workspace: Workspace;
  owner: Owner;
};
export const PermissionsTable = ({
  className,
  account,
  workspace,
  owner,
}: PermissionsTableProps) => {
  if (account) {
    return (
      <AccountTable owner={owner} account={account} className={className} />
    );
  } else {
    return (
      <WorkspaceTable
        owner={owner}
        workspace={workspace}
        className={className}
      />
    );
  }
};

///////////////////////////////////////////////////

const ownerToTableRow = (
  { full_name, photo_url }: Owner,
  grantedAt: TableRowDataProps['grantedAt'],
  { gmt, send_pulse, time }: OwnerSettings,
): TableRowDataProps => ({
  fullName: full_name,
  photoUrl: photo_url,

  admin: true,
  audience: true,
  invite_link: true,
  send_pulse,
  time,
  gmt,
  comment: 'Владелец',
  grantedAt,
});

const userToTableRow = ({
  full_name,
  photo_url,
  permission: {
    admin,
    audience,
    send_pulse,
    invite_link,
    comment,
    created_at,
    gmt,
    time,
  },
}: User): TableRowDataProps => ({
  fullName: full_name,
  photoUrl: photo_url,

  admin,
  audience,
  send_pulse,
  time,
  gmt,
  invite_link,
  comment,
  grantedAt: created_at,
});

///////////////////////////////////////////////////

type AccountTableProps = {
  owner: Owner;
  account: AdvertiserAccount;
  className?: string;
};
const AccountTable = ({ owner, account, className }: AccountTableProps) => {
  const { data } = useUsers(account);
  const { mutate } = useUpdateOwnerSettings(account);

  if (!data) {
    return <Spinner />;
  }

  const onPulseCheckedChange: TableRowProps['onPulseCheckedChange'] = (
    send_pulse,
  ) => mutate({ send_pulse });

  const onTimeChange: TableRowProps['onTimeChange'] = (time) =>
    mutate({
      time,
    });

  const onGmtChange: TableRowProps['onGmtChange'] = (gmt) =>
    mutate({
      gmt,
    });

  return (
    <Table className={className}>
      <TableRow
        {...ownerToTableRow(owner, account.created_at, data.ownerSettings)}
        onPulseCheckedChange={onPulseCheckedChange}
        onTimeChange={onTimeChange}
        onGmtChange={onGmtChange}
        ordinal={1}
      />

      {data.users.map((user, ordinal) => (
        <AccountTableRow
          key={user.user_id}
          advertiser_account_id={account.advertiser_account_id}
          ordinal={ordinal + 2}
          user={user}
        />
      ))}
    </Table>
  );
};

type AccountTableRowProps = SnakeIdObj<'advertiser_account'> &
  Pick<TableRowProps, 'ordinal'> & {
    user: User;
  };
const AccountTableRow = ({
  advertiser_account_id,
  ordinal,
  user,
}: AccountTableRowProps) => {
  const { mutate: updateUser } = useUpdateUser({
    advertiser_account_id,
    user_id: user.user_id,
  });

  const { mutate: deleteUser } = useDeleteUser({
    advertiser_account_id,
    user_id: user.user_id,
  });

  const onCheckedChange: TableRowProps['onCheckedChange'] = (key, value) =>
    updateUser({
      [key]: value,
    });

  const onCommentChange: TableRowProps['onCommentChange'] = (comment) =>
    updateUser({
      comment,
    });

  const onPulseCheckedChange: TableRowProps['onPulseCheckedChange'] = (
    send_pulse,
  ) => updateUser({ send_pulse });

  const onTimeChange: TableRowProps['onTimeChange'] = (time) =>
    updateUser({
      time,
    });

  const onGmtChange: TableRowProps['onGmtChange'] = (gmt) =>
    updateUser({
      gmt,
    });

  return (
    <TableRow
      {...userToTableRow(user)}
      ordinal={ordinal}
      onCheckedChange={onCheckedChange}
      onCommentChange={onCommentChange}
      onPulseCheckedChange={onPulseCheckedChange}
      onTimeChange={onTimeChange}
      onGmtChange={onGmtChange}
      onDelete={deleteUser}
    />
  );
};

///////////////////////////////////////////////////

type WorkspaceTableProps = {
  owner: Owner;
  workspace: Workspace;
  className?: string;
};
const WorkspaceTable = ({
  owner,
  workspace,
  className,
}: WorkspaceTableProps) => {
  const wd = workspaceMethods.getWd(workspace);
  const { data } = useWorkspaceUsers(wd);
  const { mutate } = useUpdateWorkspaceOwnerSettings(wd);

  if (!data) {
    return <Spinner />;
  }

  let onPulseCheckedChange: TableRowProps['onPulseCheckedChange'] = undefined;
  let onTimeChange: TableRowProps['onTimeChange'] = undefined;
  let onGmtChange: TableRowProps['onGmtChange'] = undefined;

  if (workspace.workspace_type === 'channel') {
    onPulseCheckedChange = (send_pulse) => mutate({ send_pulse });
    onTimeChange = (time) =>
      mutate({
        time,
      });
    onGmtChange = (gmt) =>
      mutate({
        gmt,
      });
  }

  return (
    <Table className={className}>
      <TableRow
        {...ownerToTableRow(owner, workspace.created_at, data.ownerSettings)}
        onPulseCheckedChange={onPulseCheckedChange}
        onTimeChange={onTimeChange}
        onGmtChange={onGmtChange}
        ordinal={1}
      />

      {data.users.map((user, index) => (
        <WorkspaceTableRow
          {...wd}
          user={user}
          ordinal={index + 2}
          key={user.user_id}
        />
      ))}
    </Table>
  );
};

type WorkspaceTableRowProps = WorkspaceDescriptor &
  Pick<TableRowProps, 'ordinal'> & {
    user: User;
  };
const WorkspaceTableRow = ({
  workspace_id,
  workspace_type,
  ordinal,
  user,
}: WorkspaceTableRowProps) => {
  const { mutate } = useUpdateWorkspaceUser({
    workspace_id,
    workspace_type,
    user_id: user.user_id,
  });

  const { mutate: deleteUser } = useDeleteWorkspaceUser({
    workspace_id,
    workspace_type,
    user_id: user.user_id,
  });

  const onCheckedChange: TableRowProps['onCheckedChange'] = (key, value) =>
    mutate({
      [key]: value,
    });

  const onCommentChange: TableRowProps['onCommentChange'] = (comment) =>
    mutate({
      comment,
    });

  let onPulseCheckedChange: TableRowProps['onPulseCheckedChange'] = undefined;
  let onTimeChange: TableRowProps['onTimeChange'] = undefined;
  let onGmtChange: TableRowProps['onGmtChange'] = undefined;

  if (workspace_type === 'channel') {
    onPulseCheckedChange = (send_pulse) => mutate({ send_pulse });
    onTimeChange = (time) =>
      mutate({
        time,
      });
    onGmtChange = (gmt) =>
      mutate({
        gmt,
      });
  }

  return (
    <TableRow
      {...userToTableRow(user)}
      ordinal={ordinal}
      onCheckedChange={onCheckedChange}
      onCommentChange={onCommentChange}
      onPulseCheckedChange={onPulseCheckedChange}
      onTimeChange={onTimeChange}
      onGmtChange={onGmtChange}
      onDelete={deleteUser}
    />
  );
};
